import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query"
import { api } from "@/app/api/client"

export interface NoteResponse {
  id: string
  subject_type: string
  subject_key: string
  body: string
  updated_at: string
}

interface UseNoteMutationsArgs {
  subjectType: "str"
  subjectKey: string
  /** Query keys to invalidate after a save/remove so reads recompute. */
  invalidateKeys: QueryKey[]
}

/**
 * Save/remove mutations for the per-subject note API
 * (PUT/DELETE /notes/:subject_type/:subject_key). One note per subject —
 * saving upserts, removing deletes. Subject-agnostic so future note homes
 * (wells, transactions) only add a subject type.
 */
export function useNoteMutations({
  subjectType,
  subjectKey,
  invalidateKeys,
}: UseNoteMutationsArgs) {
  const queryClient = useQueryClient()
  const path = `/notes/${subjectType}/${encodeURIComponent(subjectKey)}`
  const invalidate = () => {
    for (const queryKey of invalidateKeys) queryClient.invalidateQueries({ queryKey })
  }

  const save = useMutation({
    mutationFn: (body: string) => api.put<NoteResponse>(path, { body }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: () => api.del<void>(path),
    onSuccess: invalidate,
  })

  return { save, remove }
}
